/**
 * Care Concierge cards on the FA dashboard (Feature 106).
 *
 * Two paid paths and one free one, side by side. Sponsor spends one of the advisor's sessions and
 * is hidden entirely when they have none — a button that always fails is worse than no button.
 * Self-pay is always offered, and the no-fee service coordination call card (Feature 137) sits
 * beside them rather than under.
 */

import { useState } from 'react';
import { Check, HeartHandshake } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useToast } from '@/shared/components/Toast';
import { CalendlyModal } from '@/shared/components/CalendlyModal';
import { InviteToConsultationModal } from './InviteToConsultationModal';
import { ServiceCoordinationCallCard } from './ServiceCoordinationCallCard';
import {
  useCareConciergeCredit,
  useSendCareConciergeInvitation,
} from '../../hooks/useCareConcierge';
import type { CreateCasePayload, CasePayer } from '../../api/careConciergeApi';

const BENEFITS = [
  '60-minute consultation with an expert care advisor',
  'A clear, actionable overview of your client’s care situation',
  'Vetted local provider and resource recommendations',
];

const errorMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data
    ?.message ??
  (error as { message?: string })?.message ??
  fallback;

export const CareConciergeCard = () => {
  const { showToast } = useToast();
  const { data: credit, isLoading, isError, refetch } = useCareConciergeCredit();
  const sendInvitation = useSendCareConciergeInvitation();
  const [payer, setPayer] = useState<CasePayer | null>(null);
  /**
   * Requirement v4 item 1: set when the advisor chose to book for the client, and only then. The
   * URL comes back on the response rather than from a build-time variable, so this surface and
   * the emailed invitation can never point at different Calendly links.
   */
  const [bookingUrl, setBookingUrl] = useState<string | null>(null);

  if (isLoading) {
    return <Skeleton data-testid="care-concierge-skeleton" className="mt-6 h-48 w-full" />;
  }

  // A failed request is not the same as "this advisor has no Care Concierge". Returning null for
  // both made the whole section vanish with nothing to click, and no way to tell which it was.
  if (isError || !credit) {
    return (
      <div className="mt-6">
        <h2 className="mb-1 text-lg font-semibold text-gray-800">Care Concierge</h2>
        <p className="mb-4 text-sm text-destructive">
          We could not load your Care Concierge options.
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          <Card className="flex flex-col items-start gap-3 p-4">
            <p className="text-sm text-gray-600">
              Your sponsored sessions and the consultation options could not be loaded.
            </p>
            <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
              Try again
            </Button>
          </Card>

          {/* Never depended on the credit call, so it stays reachable. */}
          <ServiceCoordinationCallCard />
        </div>
      </div>
    );
  }

  const totalRemaining = credit.firm.remaining + credit.advisor.remaining;

  const handleConfirm = (payload: CreateCasePayload) => {
    sendInvitation.mutate(payload, {
      onSuccess: (engagement) => {
        setPayer(null);

        // Booking for the client: nothing was emailed, so there is no delivery to report and no
        // "invitation sent" to claim. The advisor finishes in Calendly.
        if (engagement.calendlyUrl) {
          setBookingUrl(engagement.calendlyUrl);
          return;
        }

        // The server may have resent an already-open invitation rather than opening a new one.
        // Saying so keeps the advisor from wondering why no second session was spent.
        if (engagement.deliveryFailed) {
          showToast(
            'The invitation could not be delivered. You can resend it from the log below.',
            'error'
          );
          return;
        }

        showToast(
          engagement.wasResend
            ? 'That client already had an open invitation, so we resent it.'
            : 'Invitation sent.',
          'success'
        );
      },
      onError: (error) => {
        showToast(errorMessage(error, 'Could not send the invitation.'), 'error');
      },
    });
  };

  // Switched off, so the paid paths are not offered. The no-fee call card stays: it predates
  // this feature, costs nothing, and is how an advisor reaches the concierge team at all.
  if (!credit.enabled) {
    return (
      <div className="mt-6">
        <h2 className="mb-1 text-lg font-semibold text-gray-800">Care Concierge</h2>
        <p className="mb-4 text-sm text-gray-600">
          Have a question about a client’s care situation?
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          <ServiceCoordinationCallCard />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <h2 className="mb-1 text-lg font-semibold text-gray-800">Care Concierge</h2>
      <p className="mb-4 text-sm text-gray-600">Put a client in front of bQuest’s care experts.</p>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="flex flex-col gap-4 p-4">
          <div className="flex items-center gap-2">
            <HeartHandshake className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-medium">Expert consultation for your client</h3>
          </div>

          <ul className="space-y-1">
            {BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-start gap-2 text-sm text-gray-600">
                <Check className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-2">
            {credit.canSponsor && (
              <Button type="button" size="sm" onClick={() => setPayer('SPONSOR')}>
                Sponsor a consultation
              </Button>
            )}
            <Button type="button" size="sm" variant="outline" onClick={() => setPayer('SELF_PAY')}>
              Recommend a self-pay consultation · {credit.selfPayFee}
            </Button>
          </div>

          {credit.canSponsor ? (
            <p className="text-xs text-muted-foreground">
              {totalRemaining} sponsored session{totalRemaining === 1 ? '' : 's'} remaining
              {credit.firm.firmName && credit.firm.available
                ? ` (${credit.firm.remaining} from ${credit.firm.firmName}, ${credit.advisor.remaining} your own)`
                : ''}
              .
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              You have no sponsored sessions left. Contact bQuest to add more, or recommend a
              self-pay consultation.
            </p>
          )}
        </Card>

        {/* The no-fee path, beside the paid one rather than buried under it. */}
        <ServiceCoordinationCallCard />
      </div>

      {bookingUrl && (
        <CalendlyModal
          open
          onOpenChange={(open) => !open && setBookingUrl(null)}
          url={bookingUrl}
          title="Book the consultation"
          description="Book on your client's behalf. The case stays open until the Care Concierge team confirms the booking."
        />
      )}

      {payer && (
        <InviteToConsultationModal
          payer={payer}
          credit={credit}
          isSending={sendInvitation.isPending}
          onConfirm={handleConfirm}
          onClose={() => setPayer(null)}
        />
      )}
    </div>
  );
};
